// FRONTEND UPDATE (Webinar.jsx)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Webinar.css';
import {
  FiClock,
  FiX,
  FiCalendar,
  FiTarget,
  FiStar,
  FiInfo,
  FiCheckCircle,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import { Modal, Button } from 'react-bootstrap';
import { CallMessaging } from '../messaging/Messaging';
import {
  API_BASE_URL,
  getAuthHeaders,
  checkTokenExpiration,
  toastdisplay,
} from '../../utils/utils';
import {
  formatWebinarDateForLocalUser,
  isWebinarJoinWindowOpen,
  isWebinarDateInFuture,
} from '../../utils/webinarTime';
import { getStoredUser } from '../../utils/authSession';

const WebinarCard = ({
  image,
  title,
  date,
  duration,
  speakers,
  onJoin,
  onView,
  timezone,
  isUpcoming,
  isRegistered,
  isJoinWindowOpen,
  webinarLink,
}) => (
  <div className="webinar-card">
    <div className="webinar-card-image-container">
      <div
        className="webinar-card-image"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="webinar-card-overlay">
        <span
          className={`webinar-status-badge ${isUpcoming ? 'upcoming' : 'passed'}`}
        >
          {isUpcoming ? 'Upcoming' : 'Past'}
        </span>
      </div>
    </div>
    <div className="webinar-card-content">
      <div className="webinar-card-header">
        <h3 className="webinar-title">{title}</h3>
        <div className="webinar-meta-info">
          <div className="webinar-date">
            <FiCalendar size={14} />
            <span>{date}</span>
          </div>
          <div className="webinar-duration">
            <FiClock size={14} />
            <span>{duration}</span>
          </div>
        </div>
        <div className="webinar-speakers">
          <span className="speakers-label">Speakers:</span>
          <span className="speakers-names">{speakers}</span>
        </div>
      </div>
      <div className="webinar-card-actions">
        {isUpcoming || isJoinWindowOpen ? (
          isRegistered ? (
            isJoinWindowOpen && webinarLink ? (
              <button
                className="btn-register"
                onClick={() => window.open(webinarLink || '#', '_blank')}
              >
                Join Now
              </button>
            ) : (
              <div className="registration-status">
                <FiCheckCircle size={16} />
                <span>Registered</span>
              </div>
            )
          ) : (
            <button className="btn-register" onClick={onJoin}>
              Register Now
            </button>
          )
        ) : (
          <button className="btn-replay" onClick={onView}>
            Watch Replay
          </button>
        )}
      </div>
    </div>
  </div>
);

const Webinar = () => {
  const [user, setUser] = useState(() => {
    return getStoredUser();
  });
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [replayUrl, setReplayUrl] = useState('');
  const [showWebinarModal, setShowWebinarModal] = useState(false);
  const [webinars, setWebinars] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const conversationsData = localStorage.getItem('conversations');
    const conversations = conversationsData
      ? JSON.parse(conversationsData)
      : [];
    console.log(conversations, 'conversations');
    setConversations(conversations);

    const fetchWebinars = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/webinars`);
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.detail || 'Failed to fetch webinars.');

        // Enhance webinar objects with isRegistered flag (only for future webinars)
        const enhanced = data.map((w) => {
          const userIsRegistered = w.registered_users?.some(
            (u) => u.email === user.email
          );
          console.log(
            userIsRegistered,
            'userIsRegistered',
            w.registered_users,
            user.email
          );
          const isFuture = isWebinarDateInFuture(w.date);
          return {
            ...w,
            isRegistered: userIsRegistered && isFuture,
          };
        });

        setWebinars(enhanced);
      } catch (error) {
        toastdisplay(error.message, false, 2000, setToast);
      }
    };

    fetchWebinars();
  }, []);

  const handleRegister = async (webinarId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/webinars/${webinarId}/register?userEmail=${user.email}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const expired = await checkTokenExpiration(response, () => {}, setToast);
      if (expired) return;

      if (!response.ok) {
        const errorData = await response.json();
        toastdisplay(
          'Failed to register for the webinar.',
          false,
          2000,
          setToast
        );
        if (!response.ok)
          throw new Error(errorData.detail || 'Failed to register.');
      }

      setWebinars((prev) =>
        prev.map((w) =>
          w.id === webinarId
            ? { ...w, registered_users: [...(w.registered_users || []), user] }
            : w
        )
      );

      toastdisplay('Successfully registered!', true, 2000, setToast);
    } catch (error) {
      toastdisplay(error.message, false, 2000, setToast);
    }
  };

  const handleWatchReplay = (url) => {
    setReplayUrl(url);
    setShowWebinarModal(true);
  };

  const handleCloseWebinarModal = () => {
    setShowWebinarModal(false);
  };

  const formatDateTime = (isoString) => {
    return formatWebinarDateForLocalUser(isoString);
  };

  return (
    <div className="layout">
      <Sidebar />
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          {toast.message}
        </div>
      )}
      <main className="content">
        {(() => {
          const upcomingWebinars = webinars.filter((w) =>
            isWebinarDateInFuture(w.date)
          );
          const pastWebinars = webinars.filter(
            (w) => !isWebinarDateInFuture(w.date)
          );

          return (
            <>
              {upcomingWebinars.length === 0 && webinars.length === 0 && (
                <div className="webinar-empty-state">
                  <div className="empty-state-content">
                    <div className="empty-state-icon">
                      <FiCalendar size={48} />
                    </div>
                    <h3>No Webinars Available</h3>
                    <p>
                      We're currently preparing exciting new webinars with top
                      graduate admissions officers and alumni.
                    </p>

                    <div className="upcoming-features">
                      <h4>What's coming soon</h4>
                      <div className="webinar-features-grid">
                        <div className="webinar-feature-item">
                          <FiTarget size={20} />
                          <span>INSEAD, WHARTON & LBS admissions insights</span>
                        </div>
                        <div className="webinar-feature-item">
                          <FiStar size={20} />
                          <span>Alumni career transformation stories</span>
                        </div>
                        <div className="webinar-feature-item">
                          <FiInfo size={20} />
                          <span>Live Q&A with admissions officers</span>
                        </div>
                        <div className="webinar-feature-item">
                          <FiTarget size={20} />
                          <span>Application strategy deep-dives</span>
                        </div>
                      </div>
                    </div>

                    <div className="notification-banner">
                      <FiInfo size={16} />
                      <span>
                        Follow our community for the latest updates and be the
                        first to register!
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="webinar-grid">
                {webinars.map((w) => (
                  <WebinarCard
                    key={w.id}
                    image={w.image}
                    title={w.title}
                    date={formatDateTime(w.date)}
                    isRegistered={w.registered_users?.some(
                      (u) => u.id === user?.id
                    )}
                    description={w.description}
                    duration={w.duration}
                    speakers={w.speakers}
                    timezone={w.timezone}
                    isUpcoming={isWebinarDateInFuture(w.date)}
                    isJoinWindowOpen={isWebinarJoinWindowOpen(w.date)}
                    webinarLink={w.webinar_link}
                    onJoin={() => handleRegister(w.id)}
                    onView={() => handleWatchReplay(w.webinar_link)}
                  />
                ))}
              </div>
            </>
          );
        })()}

        <Modal
          show={showWebinarModal}
          onHide={handleCloseWebinarModal}
          dialogClassName="liwaza-modal-webinar"
          centered
          backdrop="static"
          keyboard={false}
        >
          <div className="liwaza-modal-webinar-content">
            <div className="liwaza-modal-webinar-header">
              <Button
                variant="link"
                className="liwaza-modal-webinar-close"
                onClick={handleCloseWebinarModal}
                aria-label="Close"
              >
                <FiX size={24} color="#fff" />
              </Button>
            </div>
            <div className="liwaza-modal-webinar-body">
              <div className="webinar-embed-container">
                <iframe
                  src={replayUrl}
                  title="Webinar Replay"
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </Modal>
      </main>
    </div>
  );
};

export default Webinar;
